import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { A4_PORTRAIT_RATIO, useAspectFitWidth } from '@/utils/useAspectFitWidth';
import { JourneyCertificateHeader } from './JourneyCertificateHeader';
import { JourneyCertificatePreview } from './JourneyCertificatePreview';
import { useJourneyCertificate } from './useJourneyCertificate';
import { ROUTE_PATH } from '@/constants/routePaths';

const PageStyled = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: ${({ theme }) => theme.tones.neutral[99]};
  overflow: hidden;
`;

const ContentStyled = styled.main`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  min-width: 0;
  padding: 48px;
  overflow: hidden;
`;

export const JourneyCertificatePage = (): JSX.Element => {
  const { journeyId: journeyIdRaw, name } = useParams<{ journeyId: string; name: string }>();
  const journeyId = Number(journeyIdRaw);

  const { payload, templateId, loading, notFound, error, certificateUrl } = useJourneyCertificate(journeyId);

  const [contentRef, previewWidth] = useAspectFitWidth(A4_PORTRAIT_RATIO);

  // The backend only issues a download URL once the learner has actually earned the certificate.
  // Without it there is nothing to show, so treat the page as not found rather than letting anyone
  // preview a certificate for a journey they haven't completed.
  if (notFound) {
    return <Navigate to={ROUTE_PATH.NOT_FOUND} replace />;
  }

  return (
    <PageStyled>
      <JourneyCertificateHeader journeyId={journeyId} journeyName={name} certificateUrl={certificateUrl} />
      <ContentStyled ref={contentRef}>
        <JourneyCertificatePreview
          loading={loading}
          error={Boolean(error)}
          payload={payload}
          templateId={templateId}
          width={previewWidth}
        />
      </ContentStyled>
    </PageStyled>
  );
};
